'use client';

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,  } from 'recharts';

// Backend integration point: GET /api/ehs/industrial-hygiene/exposure-samples
const exposureData = [
  { id: 'ih-benzene', chemical: 'Benzene', twa: 0.4, pel: 1.0, stel: 5.0, unit: 'ppm', exceeded: false },
  { id: 'ih-h2s', chemical: 'H₂S', twa: 8.2, pel: 20.0, stel: 50.0, unit: 'ppm', exceeded: false },
  { id: 'ih-ammonia', chemical: 'Ammonia', twa: 24.1, pel: 25.0, stel: 35.0, unit: 'ppm', exceeded: false },
  { id: 'ih-toluene', chemical: 'Toluene', twa: 112, pel: 100, stel: 150, unit: 'ppm', exceeded: true },
  { id: 'ih-silica', chemical: 'Silica dust', twa: 0.08, pel: 0.05, stel: null, unit: 'mg/m³', exceeded: true },
  { id: 'ih-noise', chemical: 'Noise (8hr)', twa: 83, pel: 90, stel: null, unit: 'dBA', exceeded: false },
  { id: 'ih-co', chemical: 'Carbon Monoxide', twa: 28, pel: 50, stel: 200, unit: 'ppm', exceeded: false },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const row = exposureData.find((d) => d.chemical === label);
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg px-4 py-3 shadow-xl text-xs">
      <p className="font-600 text-[hsl(var(--foreground))] mb-2">{label}</p>
      {payload.map((entry: any) => (
        <div key={`ih-tt-${entry.dataKey}`} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-[hsl(var(--muted-foreground))]">{entry.name}:</span>
          <span className="font-600 tabular-nums">
            {entry.value} {row?.unit}
          </span>
        </div>
      ))}
      {row?.exceeded && (
        <p className="text-red-500 font-600 mt-1.5">⚠ Exceeds PEL threshold</p>
      )}
    </div>
  );
};

export default function IHExposureChart() {
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">Industrial Hygiene — Exposure vs PEL</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            TWA measured vs OSHA Permissible Exposure Limit · Q2 2026
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-500/10 border border-red-500/20">
          <span className="text-[11px] font-600 text-red-500">2 exceedances</span>
        </div>
      </div>

      <div className="flex-1 min-h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={exposureData}
            margin={{ top: 8, right: 8, left: -8, bottom: 40 }}
            barCategoryGap="30%"
            barGap={3}
          >
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" strokeOpacity={0.5} vertical={false} />
            <XAxis
              dataKey="chemical"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
              angle={-30}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
            />
            <Bar dataKey="twa" name="TWA Measured" radius={[3, 3, 0, 0]} maxBarSize={20}>
              {exposureData.map((entry) => (
                <Cell
                  key={`ih-cell-${entry.id}`}
                  fill={entry.exceeded ? '#ef4444' : '#f59e0b'}
                  fillOpacity={0.85}
                />
              ))}
            </Bar>
            <Bar dataKey="pel" name="PEL Limit" fill="#64748b" fillOpacity={0.35} radius={[3, 3, 0, 0]} maxBarSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-[hsl(var(--muted-foreground))]">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-500/85" />
          TWA within PEL
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-500/85" />
          TWA exceeds PEL
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-slate-500/35" />
          PEL Limit
        </span>
      </div>
    </div>
  );
}